import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import Input from '../components/ui/Input';
import MarketingNavbar from '../components/marketing/MarketingNavbar';
import { getPasswordStrength } from '../utils/passwordStrength';

const RequestTenantPage = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    businessName: '',
    ownerName: '',
    phone: '',
    address: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const strength = getPasswordStrength(form.password);

  const strengthLabel = (label) => {
    const map = {
      Weak: t('password.weak'),
      Fair: t('password.fair'),
      Good: t('password.good'),
      Strong: t('password.strong'),
    };
    return map[label] || label;
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const next = () => {
    setError('');
    if (step === 1 && (!form.businessName || !form.ownerName || !form.email)) {
      setError(t('tenantRequest.fillRequired'));
      return;
    }
    if (step === 2) {
      if (form.password.length < 8) {
        setError(t('tenantRequest.passwordMinLength'));
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError(t('tenantRequest.passwordMismatch'));
        return;
      }
    }
    setStep(step + 1);
  };

  const submit = async () => {
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/request-tenant', {
        businessName: form.businessName,
        ownerName: form.ownerName,
        email: form.email,
        phone: form.phone,
        address: form.address,
        password: form.password,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || t('tenantRequest.submissionFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col bg-zinc-50 dark:bg-zinc-950">
        <MarketingNavbar />
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="max-w-md rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-10 text-center">
            <h1 className="text-2xl font-semibold text-glow-400">{t('tenantRequest.successTitle')}</h1>
            <p className="mt-6 text-zinc-700 dark:text-zinc-300">
              {t('tenantRequest.successMessage')}
            </p>
            <Link to="/" className="mt-8 inline-block text-sm text-glow-400 hover:underline">
              {t('tenantRequest.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <MarketingNavbar />
      <div className="mx-auto max-w-lg px-6 py-12">
        <h1 className="text-2xl font-semibold">{t('tenantRequest.title')}</h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">{t('tenantRequest.applySubtitle')}</p>

        <div className="mt-8">
          <div className="mb-2 flex justify-between text-xs text-zinc-600 dark:text-zinc-500">
            <span>{t('common.stepOf', { step, total: 3 })}</span>
            <span>{Math.round((step / 3) * 100)}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
            <div
              className="h-full bg-glow-600 transition-all"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>

        {error && (
          <p className="mt-4 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</p>
        )}

        <div className="mt-8 space-y-4">
          {step === 1 && (
            <>
              <Input label={t('tenantRequest.businessNameRequired')} id="businessName" value={form.businessName} onChange={update('businessName')} required />
              <Input label={t('tenantRequest.ownerNameRequired')} id="ownerName" value={form.ownerName} onChange={update('ownerName')} required />
              <Input label={t('tenantRequest.businessEmailRequired')} id="email" type="email" value={form.email} onChange={update('email')} required />
              <Input label={t('tenantRequest.phone')} id="phone" value={form.phone} onChange={update('phone')} />
              <Input label={t('tenantRequest.address')} id="address" value={form.address} onChange={update('address')} />
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <Input label={t('tenantRequest.passwordRequired')} id="password" type="password" value={form.password} onChange={update('password')} required />
                {form.password && (
                  <div className="mt-2">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded ${i <= strength.score ? strength.color : 'bg-zinc-100 dark:bg-zinc-800'}`}
                        />
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">{strengthLabel(strength.label)}</p>
                  </div>
                )}
              </div>
              <Input label={t('tenantRequest.confirmPasswordRequired')} id="confirmPassword" type="password" value={form.confirmPassword} onChange={update('confirmPassword')} required />
            </>
          )}

          {step === 3 && (
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 text-sm">
              <h3 className="font-medium text-zinc-800 dark:text-zinc-200">{t('tenantRequest.reviewTitle')}</h3>
              <dl className="mt-4 space-y-2 text-zinc-600 dark:text-zinc-400">
                <div className="flex justify-between"><dt>{t('tenantRequest.reviewBusiness')}</dt><dd className="text-zinc-800 dark:text-zinc-200">{form.businessName}</dd></div>
                <div className="flex justify-between"><dt>{t('tenantRequest.reviewOwner')}</dt><dd className="text-zinc-800 dark:text-zinc-200">{form.ownerName}</dd></div>
                <div className="flex justify-between"><dt>{t('common.email')}</dt><dd className="text-zinc-800 dark:text-zinc-200">{form.email}</dd></div>
                <div className="flex justify-between"><dt>{t('common.phone')}</dt><dd className="text-zinc-800 dark:text-zinc-200">{form.phone || '—'}</dd></div>
                <div className="flex justify-between"><dt>{t('common.address')}</dt><dd className="text-zinc-800 dark:text-zinc-200">{form.address || '—'}</dd></div>
              </dl>
            </div>
          )}
        </div>

        <div className="mt-8 flex gap-3">
          {step > 1 && (
            <button type="button" onClick={() => setStep(step - 1)} className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm">
              {t('common.back')}
            </button>
          )}
          {step < 3 ? (
            <button type="button" onClick={next} className="flex-1 rounded-lg bg-glow-600 py-2 text-sm font-medium text-white hover:bg-glow-500">
              {t('common.continue')}
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={loading} className="flex-1 rounded-lg bg-glow-600 py-2 text-sm font-medium text-white hover:bg-glow-500 disabled:opacity-50">
              {loading ? t('tenantRequest.submitting') : t('tenantRequest.submitApplication')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestTenantPage;
